import { useTranslation } from "react-i18next";
import { useSiteLevels } from "../reducers/levelSlice";
import useClientConfig from "../common/hooks/clientConfigHook";
import useCredentials from "../common/hooks/credentialsHook";
import Select from "../common/components/Select";
import PropTypes from 'prop-types';
import { useMemo } from "react";
import { dynamicSort } from "../common/utils/tableUtilities";
import { distinct } from "../common/utils/arrayUtilities";
import { useTypes } from "../reducers/typeSlice";
import Label from "../common/components/Label";
import { propagateFields } from "./customCtrlUtils";

export const customCtrlDef = {
    control: ({ def, onChange, site, level, type, context: { args } = {}, ...props }) => 
        <LevelDropdown site={site ?? args?.site ?? def.site} level={level} type={type ?? args?.type ?? def.typeParam} perm={def.perm} wildcard={def.wildcard} filter={def.filter} strict={def.strict} mode={def.mode} credentialsRoot={def.credentialsRoot} onChange={value => propagateFields(value, def.field, onChange)} {...props}></LevelDropdown>
}

/**
 * A dropdown to select a level given a currently selected site, if any.
 * @component
 */
const LevelDropdown = ({ id, site, level, type, perm, placeholder, wildcard, hideLabel, disabled = false, required = false, readonly = false, onChange, credentialsRoot = 'access', strict = true, autoHide = false, filter }) => {
    const clientConfig = useClientConfig();
    const credentials = useCredentials();
    const { t } = useTranslation();
    const levels = useSiteLevels(site);
    const types = useTypes();

    const userLevels =  useMemo(() => {
        const access = credentials.access.accessTriples(clientConfig, 'full');
        return distinct(access?.map(a => a.level));
    }, [clientConfig, credentials]);
    
    const hideControl = useMemo(() => autoHide && perm === 'write' && userLevels.length === 1 && level && userLevels[0] === level, [autoHide, level, perm, userLevels]);

    const displayLevels = useMemo(() => {
        let displayLevels = levels.filter(l => (!filter || filter(l)) && ((l.name === level && (!l.site || !site || l.site === site)) || perm === 'any' || credentials[credentialsRoot][perm === 'write' ? 'hasFull' : 'has']({ site: l.site ?? site, level: l.name })));
        if (level && displayLevels.every(l => l.name !== level || (l.site && site && l.site !== site))) {
            const toAdd = levels.find(l => l.name === level && (!l.site || !site || l.site === site));
            if (toAdd) {
                displayLevels.push(toAdd);
            }
        }
        displayLevels.sort(dynamicSort(['sequence','name','site']));
        if (!strict) {
            if (type) {
                displayLevels = displayLevels.filter(l => types.some(t => t.type === type && (!t.level || t.level === l.name) && (!t.site || !l.site || t.site === l.site)));
            }
            displayLevels = displayLevels.map(level => ({ name: level.name, internalid: level.internalid }));
            displayLevels = distinct(displayLevels, 'name');
        }
        return displayLevels;
    }, [levels, level, strict, filter, site, perm, credentials, credentialsRoot, type, types]);

    const handleChange = value => {
        const level = displayLevels.find(l => l.internalid === value)
        if (!level) {
            onChange({ level: null });
        } else {
            let update = { level: level.name };
            if (strict && clientConfig.enableSites && level.site && level.site !== site) {
                update.site = level.site;
            }
            onChange(update);
        }
    }

    const options = useMemo(() => displayLevels.map(l => ({ value: l.internalid, label: ((strict && clientConfig.enableSites && !site && l.site) ? l.site + ' - ' : '') + l.name})), [displayLevels, strict, clientConfig.enableSites, site]);

    const levelDetails = displayLevels.find(l => l.name === level && (!strict || clientConfig.enableSites || !l.site || l.site === site));
    if (hideControl) {
        return
    }
    if (readonly) {
        return <Label placeholder={placeholder} hideLabel={hideLabel} text={level ?? wildcard}></Label>
    }

    return (<Select id={id} placeholder={placeholder ?? t('level', { count: 1 })} wildcard={wildcard} hideLabel={hideLabel} value={levelDetails?.internalid} onChange={handleChange} disabled={disabled} required={required} options={options} labelField="label" valueField="value"></Select>)
}

LevelDropdown.propTypes = {
    /**
     * The currently selected site, if any
     */
    site: PropTypes.string,

    /**
     * The currently selected level, if any
     */
    level: PropTypes.string,

    /**
     * Only list levels to which the user has appropriate access
     */
    perm: PropTypes.oneOf(['any','read','write']),

    /**
     * The label for the control
     */
    placeholder: PropTypes.string,

    /**
     * The control must have a value for the from in which it is contained to validate
     */
    required: PropTypes.bool,

    /**
     * Force the input to be read-only
     */
    disabled: PropTypes.bool,

    /**
     * Collapses the floating label used for spacing
     */
    hideLabel: PropTypes.bool,

    /**
     * Function to call when the selected level has been changed - value is returned in an object under the level field which may also have a site field
     */
    onChange: PropTypes.func.isRequired,
    /**
     * When provided indicates the dropdown should allow a null selection which is to be treated as a wildcard.
     * The string supplied is used as the label of the wildcard entry (standard use would be something like
     * '(any)' or '(all)' depending on the context)
     */
    wildcard: PropTypes.string,
    /**
     * When set to false the dropdown will ignore site matching and only return the name of a level.
     * This is for use when wildcard matching against sites is required - this situation requires strict = false
     */
    strict: PropTypes.bool,
     /**
      * A filter function to gate whether a level is available as an option.
      * Takes a single argument - a level - and return true to include the level in
      * the available selections.
      */
    filter: PropTypes.func,
}

export default LevelDropdown;